import React, {FC, useMemo, useState} from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
  useTheme,
} from "@material-ui/core";
import {grey} from "@material-ui/core/colors";
import CircleIcon from "@material-ui/icons/RadioButtonUnchecked";
// Same glyph our `Alert severity="warning"` uses, so the row marker and the
// warning banners it belongs to read as one signal.
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import UnfoldMoreIcon from "@material-ui/icons/UnfoldMoreOutlined";
import UnfoldLessIcon from "@material-ui/icons/UnfoldLessOutlined";
import clsx from "clsx";
import {T, useTranslate} from "@tolgee/react";
import useDateFnsLocale from "hooks/useDateFnsLocale";

import {
  IAnonymousShowProjection,
  IGetOneByIdTaskOption,
} from "@evennt-tech/api-v2-external";
import type {MultitaskSubtaskRow} from "view/components/TasksList/components/TaskDetailsDrawer/helpers/drawerStateAdapters";
import {hasAnswerPayload} from "view/components/TasksList/components/TaskDetailsDrawer/helpers/drawerStateAdapters";
import {
  ICustomField,
  IStandToCustomField,
  TaskResponseType,
  TaskStatus,
  Theme,
} from "@evennt-tech/api-v2-core";
import {useCurrentShowId} from "hooks";
import useBranding from "store/branding/branding.store";

import {IStandTaskListRow} from "ts/api/stand-task-list-row";

import RichText from "view/components/RichText";
import FfairPaper from "view/components/FfairPaper";
import FlexBox from "view/components/Layout/FlexBox";
import CheckIcon from "view/images/CheckInCircleIcon";
import ResponseBlock from "view/components/TasksList/components/ResponseBlock";
import getTextWithVariables from "tools/task/getTextWithVariables";
import {generateTestId} from "tools/cypress/generateTestId";

import ModalSectionHeader from "../Drawer/ModalSectionHeader";
import useStyles from "./styles";

/**
 * Read-only multitask subtask list for the exhibitor flow. Each expanded
 * subtask reuses `ResponseBlock` so it renders with the same per-type
 * renderer as a non-multitask task's active response.
 */
type Props = {
  subtasks: MultitaskSubtaskRow[];
  show: IAnonymousShowProjection<string>;
  parentRow: IStandTaskListRow;
  parentStatus: TaskStatus;
  customFields: ICustomField[];
  /**
   * Per-subtask option definitions keyed by subtask id. Required for
   * MC / CHECKBOXES subtasks to render human-readable labels (incl.
   * nested picks); without it ResponseBlock falls back to raw uuids.
   */
  subtaskOptionsById?: Record<string, IGetOneByIdTaskOption[]>;
  localizedOrganiser?: string | null;
  readonly?: boolean;
  /**
   * Render GRID subtask responses INLINE (full table) instead of the cut/link
   * version that opens the standalone `/shows/:showId/preview-grid` window.
   * Required for the anonymous email-review page: that preview route is a
   * `PrivateRoute`, so a reviewer with no session can't open it — they must
   * see the grid inline. Defaults to the link behaviour for authed drawers.
   */
  inlineGrid?: boolean;
  /**
   * Per-stand custom-field values so subtask description `<custom field>`
   * placeholders resolve. Inherited from the parent's stand.
   */
  standToCustomFields?: IStandToCustomField[];
  /**
   * Organiser-defined labelled dividers between subtasks. `orderIndex`
   * shares the subtasks' `orderInParent` axis, so a separator renders
   * before the first subtask whose order exceeds it.
   */
  separators?: SubtaskSeparatorLike[];
  /**
   * Subtask ids whose committed answer is newer than the parent submit —
   * `drawerState.changedAfterSubmitSubtaskIds`, computed server-side from the
   * same comparison as the parent's `needsResubmit`. Marks those rows
   * "Updated" so a reviewer can see WHICH answer moved on, not just that one
   * did. Empty / omitted when nothing changed.
   */
  changedAfterSubmitSubtaskIds?: string[];
  onClickSubtask: (subtaskId: string) => void;
  onClickHistory?: (subtaskId: string) => void;
};

type SubtaskSeparatorLike = {orderIndex: number; label?: string | null};

/**
 * Answered means a non-draft response that carries an answer (or is marked
 * Not Applicable). Instance status alone isn't enough: a DONE parent cascades
 * `done` onto every subtask, including an unfilled optional one that ends up
 * with a status-only response but an empty payload — that must read as TODO.
 */
const isSubtaskAnswered = (s: MultitaskSubtaskRow): boolean =>
  !!s.response &&
  !(s.response.isDraft ?? false) &&
  (!!s.response.isNotApplicable || hasAnswerPayload(s.responseType, s.payload));

const TaskSubtaskList: FC<Props> = ({
  subtasks,
  show,
  parentRow,
  parentStatus,
  customFields,
  subtaskOptionsById,
  localizedOrganiser,
  readonly,
  separators,
  changedAfterSubmitSubtaskIds,
  standToCustomFields,
  onClickSubtask,
  onClickHistory,
  inlineGrid,
}) => {
  const classes = useStyles();
  const {t} = useTranslate();
  const locale = useDateFnsLocale();
  const theme = useTheme();
  const showId = useCurrentShowId();
  const {getBranding} = useBranding();
  const isDarkTheme = getBranding(showId)?.theme === Theme.DARK;
  const [expanded, setExpanded] = useState<string[]>([]);
  const changedAfterSubmit = useMemo(
    () => new Set(changedAfterSubmitSubtaskIds ?? []),
    [changedAfterSubmitSubtaskIds]
  );

  // Interleave the labelled separators with the subtasks: each renders
  // before the first subtask whose order exceeds its `orderIndex`; any
  // left over render at the end.
  const {separatorsBySubtaskId, trailingSeparators} = useMemo(() => {
    const sorted = [...(separators ?? [])].sort(
      (a, b) => a.orderIndex - b.orderIndex
    );
    const bySubtask: Record<string, SubtaskSeparatorLike[]> = {};
    let p = 0;
    for (const s of subtasks) {
      const order = s.orderInParent ?? 0;
      const list: SubtaskSeparatorLike[] = [];
      while (p < sorted.length && sorted[p].orderIndex <= order) {
        list.push(sorted[p]);
        p += 1;
      }
      bySubtask[s.id] = list;
    }
    return {
      separatorsBySubtaskId: bySubtask,
      trailingSeparators: sorted.slice(p),
    };
  }, [subtasks, separators]);

  const renderSeparator = (sep: SubtaskSeparatorLike, key: string) => (
    <Box key={key} className={classes.separator}>
      {!!sep.label && (
        <Typography className={classes.separatorTitle}>{sep.label}</Typography>
      )}
    </Box>
  );

  const expandableIds = useMemo(
    () => subtasks.filter(isSubtaskAnswered).map((s) => s.id),
    [subtasks]
  );
  const allExpanded =
    expandableIds.length > 0 && expanded.length === expandableIds.length;
  const noneFilled = expandableIds.length === 0;

  const onToggle = (s: MultitaskSubtaskRow, answered: boolean) => {
    if (answered) {
      setExpanded((prev) =>
        prev.includes(s.id) ? prev.filter((id) => id !== s.id) : [...prev, s.id]
      );
      return;
    }
    // Not answered (empty or draft): don't expand an empty block — open
    // the response modal directly so the user can fill it in.
    if (!readonly) {
      onClickSubtask(s.id);
    }
  };

  const onClickExpandAll = () => {
    setExpanded(allExpanded ? [] : expandableIds);
  };

  const renderIcon = (s: MultitaskSubtaskRow) => {
    // DONE check only for a subtask that carries an answer; an unfilled
    // optional subtask under a DONE parent reads as TODO (open circle).
    if (!isSubtaskAnswered(s)) {
      return <CircleIcon htmlColor={grey[500]} />;
    }
    return (
      <CheckIcon
        className={
          parentStatus !== TaskStatus.TODO
            ? classes.checkCompleted
            : classes.checkUncompleted
        }
      />
    );
  };

  const subtasksTitle = t("common.tasks.subtasks.title");

  return (
    <Box marginBottom={3}>
      <ModalSectionHeader
        title={subtasksTitle}
        actionPanel={
          <Button
            color="primary"
            disabled={noneFilled}
            className={classes.expandAllButton}
            onClick={onClickExpandAll}
            endIcon={allExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
          >
            <T
              keyName={
                allExpanded
                  ? "common.tasks.subtasks.collapse_all"
                  : "common.tasks.subtasks.expand_all"
              }
            />
          </Button>
        }
      />
      <FfairPaper className={classes.paper}>
        <List
          component="nav"
          aria-label={subtasksTitle}
          className={classes.subTaskAccordion}
        >
          {subtasks.map((s, idx) => {
            const isAnswered = isSubtaskAnswered(s);
            // Disabled = not expandable; clicking opens the response modal.
            const isDisabled = !isAnswered;
            // Drop the top border so a divider + the subtask below it read
            // as one group.
            const isFirstAfterSeparator =
              (separatorsBySubtaskId[s.id]?.length ?? 0) > 0;
            const accordionClasses = {
              root: clsx(classes.rootAccordion, {
                [classes.disabled]: isDisabled,
                [classes.accordionNoBorder]: isFirstAfterSeparator,
                [classes.cursorDefaultValue]: readonly,
              }),
              expanded: clsx(classes.expandedAccordion, {
                [classes.expandedAccordionBorder]: !isFirstAfterSeparator,
              }),
            };
            const status = s.instance?.status ?? TaskStatus.TODO;
            const isDraft = s.response?.isDraft ?? false;
            const isLast = idx === subtasks.length - 1;
            const descriptionHtml = s.description
              ? getTextWithVariables({
                  task: {stand: parentRow.stand, standToCustomFields},
                  show,
                  customFields,
                  description: s.description,
                  // Variables are stamped with a fixed dark colour; only swap
                  // it in dark theme so the light rendering is untouched.
                  contrastColor: isDarkTheme
                    ? theme.palette.contrastText.main
                    : undefined,
                  localizedOrganiser,
                  t,
                  locale,
                })
              : "";
            return (
              <React.Fragment key={s.id}>
                {(separatorsBySubtaskId[s.id] ?? []).map((sep, si) =>
                  renderSeparator(sep, `sep-${s.id}-${si}`)
                )}
                <Box
                  className={classes.row}
                  onClick={() => onToggle(s, isAnswered)}
                >
                  <Box className={classes.subtaskIconBox}>{renderIcon(s)}</Box>
                  <Accordion
                    elevation={0}
                    expanded={expanded.includes(s.id)}
                    classes={accordionClasses}
                  >
                    <AccordionSummary
                      classes={{
                        expanded: classes.expandedSummary,
                        expandIcon: classes.expandedSummaryIcon,
                        disabled: classes.disabledSummary,
                      }}
                      expandIcon={
                        <ExpandMoreIcon
                          className={
                            isDisabled ? classes.disableExpandIcon : undefined
                          }
                        />
                      }
                    >
                      <ListItem key={s.id} className={classes.subTaskAccordion}>
                        <ListItemText
                          classes={{
                            root: clsx({
                              // Strike through only a genuinely-answered DONE
                              // subtask, not an unfilled optional one.
                              [classes.lineThrough]:
                                status === TaskStatus.DONE && isAnswered,
                            }),
                          }}
                          primary={
                            <FlexBox gap={1} alignCenter>
                              <Typography variant="h4">
                                {s.title ?? ""}
                              </Typography>
                              {/* This sub-task's own answer is newer than the
                                  parent submit — server-computed, so it can't
                                  contradict the parent's resubmit banner. */}
                              {changedAfterSubmit.has(s.id) && (
                                <Tooltip title="Updated">
                                  <InfoOutlinedIcon
                                    className={classes.updatedIcon}
                                    // Inline, not a class — see `updatedIcon`.
                                    style={{
                                      color: theme.palette.warning.main,
                                      fill: theme.palette.warning.main,
                                    }}
                                    {...generateTestId(
                                      "subtask-updated-after-submit"
                                    )}
                                  />
                                </Tooltip>
                              )}
                              {isDraft && (
                                <Typography className={classes.draftTitle}>
                                  <T keyName="common.tasks.subtasks.draft_suffix" />
                                </Typography>
                              )}
                            </FlexBox>
                          }
                        />
                        <ListItemText
                          style={{flex: "150px"}}
                          classes={{primary: classes.accordionOptionTasksText}}
                          primary={
                            s.required
                              ? t("common.tasks.subtasks.required")
                              : t("common.tasks.subtasks.optional")
                          }
                        />
                      </ListItem>
                    </AccordionSummary>
                    <AccordionDetails
                      className={classes.accordionDetails}
                      // The toggle lives on the outer row Box (wrapping both
                      // summary and details), so stop the bubble here or a
                      // click inside the expanded content collapses the
                      // subtask. Inner controls still work — their handlers
                      // run before the bubble reaches this boundary.
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Box
                        className={clsx(
                          {[classes.notBorder]: isLast},
                          classes.details
                        )}
                      >
                        {descriptionHtml && (
                          <RichText
                            content={descriptionHtml}
                            className={clsx({
                              [classes.description]: !!descriptionHtml.length,
                              [classes.descriptionDark]: isDarkTheme,
                            })}
                          />
                        )}
                        {s.responseType && (
                          <FfairPaper className={classes.paperDetails}>
                            <ResponseBlock
                              isSubtask
                              grid={{cutVersion: !inlineGrid}}
                              headCellSize="small"
                              // GRID subtasks inherit the parent row's stand /
                              // company / due date to populate the grid preview
                              // header (the subtask projection has none). Scoped
                              // to GRID so other types keep `task` undefined and
                              // resolve labels from `apiV2.options`.
                              task={
                                s.responseType === TaskResponseType.GRID
                                  ? ({
                                      // Drives the grid preview header name.
                                      title: s.title ?? null,
                                      stand: parentRow.stand,
                                      company: parentRow.company,
                                      resolvedDueDate:
                                        parentRow.resolvedDueDate ?? null,
                                    } as never)
                                  : undefined
                              }
                              apiV2={{
                                responseType: s.responseType,
                                payload: s.payload,
                                hasHistory: !!s.response,
                                responseId: s.response?.id,
                                gridSchema: s.gridSchema ?? null,
                                options: subtaskOptionsById?.[s.id],
                                // Drives the "Not Applicable" display; without
                                // it ResponseBlock bails on the empty payload
                                // and the expanded subtask renders blank.
                                isNotApplicable: !!s.response?.isNotApplicable,
                              }}
                              onClickHistoryButton={
                                onClickHistory
                                  ? () => onClickHistory(s.id)
                                  : undefined
                              }
                              // Opens the subtask response modal; gated by
                              // `!readonly`.
                              onClickEdit={
                                readonly
                                  ? undefined
                                  : () => onClickSubtask(s.id)
                              }
                            />
                          </FfairPaper>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Box>
              </React.Fragment>
            );
          })}
          {trailingSeparators.map((sep, si) =>
            renderSeparator(sep, `sep-tail-${si}`)
          )}
        </List>
      </FfairPaper>
    </Box>
  );
};

export default TaskSubtaskList;
